import classNames from 'classnames';
import {
  AddressBlock,
  CirclePicture,
  ContentBlock,
  FlexContainer,
  MapRender,
  TimeLine,
  TitledBlock,
  ToDoHeading,
  Txt,
  Icon,
  IconTooltip,
  PropertyBaseDetails,
} from 'components';
import { observer } from 'mobx-react-lite';
import { Marker } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import {
  depositStatusMap,
  tenancyAgreementStatusMap,
  tenancyAgreementStatusTypes,
} from 'config/types';
import { fromSwiftDate } from 'helpers/swiftDateFormatter';
import { useTenancy } from 'state';
import styles from './TenancyViewDetails.module.scss';

/**
 * TenancyViewDetails
 *
 * @return {JSX.Element}
 */
const TenancyViewDetails = ({ className }) => {
  const navigate = useNavigate();
  const tenancyDoc = useTenancy();
  const { property: propertyDoc } = tenancyDoc;
  const { tenants: tenantsCollection } = tenancyDoc;

  const tenancy = tenancyDoc.data;
  const property = propertyDoc.data;
  const tenants = tenantsCollection.data;

  const latLng = {
    lat: property.address?.lat,
    lng: property.address?.long,
  };

  const tenancyStartDate = fromSwiftDate(tenancy.dates?.startDate);
  const tenancyEndDate = fromSwiftDate(tenancy.dates?.endDate);
  const isAgreementSigned =
    tenancy.agreement?.status === tenancyAgreementStatusTypes.signed;

  const handleGoToTodo = () => navigate('../../todo');

  const AgreementWrap = ({ children }) => {
    if (isAgreementSigned) {
      return (
        <a
          className={styles.ButtonLink}
          href={tenancy.agreement.pdfDocumentUrl}
          target="_blank"
          rel="noreferrer"
        >
          {children}
        </a>
      );
    }

    return children;
  };

  const todoItems = [
    tenancyDoc.agreementSigned,
    tenancyDoc.tenantsDepositPaid,
    tenancyDoc.tenantsStandingOrdersSetup,
    tenancyDoc.propertyIsOccupied,
  ];

  const toItemsCompleted = todoItems.filter(Boolean);

  return (
    <FlexContainer
      className={classNames(
        styles.TenancyViewDetails,
        className,
        TenancyViewDetails.displayName
      )}
      column
      gap
    >
      <ToDoHeading
        description1="Todo List"
        description2="Here you can manage all of your tenancy tasks in one place."
        completed={toItemsCompleted.length}
        completedOf={todoItems.length}
        onClick={handleGoToTodo}
        black
        noDivider
      />
      <TitledBlock title="Tenants">
        <FlexContainer gap wrap>
          {tenants?.length ? (
            tenants.map((t) => (
              <ContentBlock alignCenter key={t.id} gap>
                <CirclePicture
                  fullText={`${t.data.firstName} ${t.data.lastName}`}
                />
                <FlexContainer column>
                  <Txt>{t.data.fullName}</Txt>
                  <FlexContainer
                    gapHalf
                    alignCenter
                    className={styles.TenantContactBlock}
                  >
                    <a
                      href={`mailto:${t.data.emailAddress}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon name="envelope" />
                    </a>
                    <a
                      href={`tel:${t.data.mobileNumber}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon name="phone" />
                    </a>
                    {t.data.hasGuarantor ? (
                      <IconTooltip name="secure">
                        <ContentBlock alignCenter gap flex>
                          <CirclePicture
                            fullText={`${t.data.guarantor.firstName} ${t.data.guarantor.lastName}`}
                          />
                          <FlexContainer column>
                            <Txt noWrap>{t.data.guarantor.fullName}</Txt>
                            <FlexContainer
                              gapHalf
                              alignCenter
                              className={styles.TenantContactBlock}
                            >
                              <a
                                href={`mailto:${t.data.guarantor.emailAddress}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Icon name="envelope" />
                              </a>
                              <a
                                href={`tel:${t.data.guarantor.mobileNumber}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Icon name="phone" />
                              </a>
                            </FlexContainer>
                          </FlexContainer>
                        </ContentBlock>
                      </IconTooltip>
                    ) : null}
                  </FlexContainer>
                </FlexContainer>
              </ContentBlock>
            ))
          ) : (
            <Txt regular>No tenants</Txt>
          )}
        </FlexContainer>
      </TitledBlock>
      <TitledBlock title="Timeline">
        <ContentBlock column>
          <TimeLine dateStart={tenancyStartDate} dateEnd={tenancyEndDate} />
        </ContentBlock>
      </TitledBlock>
      <TitledBlock title="Tenancy agreement">
        <AgreementWrap>
          <ContentBlock alignCenter flex>
            <FlexContainer column gapHalf flex>
              <Txt m caption>
                STATUS -{' '}
                {(
                  tenancyAgreementStatusMap[tenancy.agreement?.status] ||
                  'Unknown'
                ).toUpperCase()}
              </Txt>
              <Txt xx>
                {isAgreementSigned ? 'Tap to view' : 'Available once signed'}
              </Txt>
            </FlexContainer>
            <FlexContainer>
              <Txt caption>
                <Icon name="secure" />
              </Txt>
            </FlexContainer>
          </ContentBlock>
        </AgreementWrap>
      </TitledBlock>
      <TitledBlock title="Deposit account number">
        <ContentBlock alignCenter>
          <FlexContainer column gapHalf flex>
            <Txt m caption>
              STATUS - {depositStatusMap[tenancy.deposit?.status] || 'Unknown'}
            </Txt>
            <Txt xx>
              {tenancy.deposit.tdsDepositAccountNumber
                ? tenancy.deposit.tdsDepositAccountNumber
                : 'EW0000000'}
            </Txt>
          </FlexContainer>
          <FlexContainer>
            <Txt caption>
              <Icon name="secure" />
            </Txt>
          </FlexContainer>
        </ContentBlock>
      </TitledBlock>

      <TitledBlock title="Address">
        <FlexContainer gap column>
          <AddressBlock address={property.address} />
          <MapRender center={latLng} zoom={14}>
            <Marker position={latLng} />
          </MapRender>
        </FlexContainer>
      </TitledBlock>
      <TitledBlock title="Certificates">
        <FlexContainer column gap>
          <ContentBlock spaceBetween alignCenter>
            <Txt>Energy Performance Certificate (EPC)</Txt>
            <Icon name="documentOutline" />
          </ContentBlock>
          <ContentBlock spaceBetween alignCenter>
            <Txt>Electrical Installation Condition Report (EICR)</Txt>
            <Icon name="documentOutline" />
          </ContentBlock>
          <ContentBlock spaceBetween alignCenter>
            <Txt>Gas Safety Certificate (GSC)</Txt>
            <Icon name="documentOutline" />
          </ContentBlock>
        </FlexContainer>
      </TitledBlock>
      <PropertyBaseDetails property={property} />
    </FlexContainer>
  );
};

TenancyViewDetails.displayName = 'TenancyViewDetails';

export default observer(TenancyViewDetails);
